/*
  The MIT License

  Copyright (c) 2017-2019 EclipseSource Munich
  https://github.com/eclipsesource/jsonforms

  Copyright (c) 2020 headwire.com, Inc
  https://github.com/headwirecom/jsonforms-react-spectrum-renderers

  Permission is hereby granted, free of charge, to any person obtaining a copy
  of this software and associated documentation files (the "Software"), to deal
  in the Software without restriction, including without limitation the rights
  to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
  copies of the Software, and to permit persons to whom the Software is
  furnished to do so, subject to the following conditions:

  The above copyright notice and this permission notice shall be included in
  all copies or substantial portions of the Software.

  THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
  IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
  FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
  AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
  LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
  OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
  THE SOFTWARE.
*/
import { Flex } from '@adobe/react-spectrum';
import { ControlProps } from '@jsonforms/core';
import { useJsonForms, withJsonFormsControlProps } from '@jsonforms/react';
import React, { useState, useEffect } from 'react';
import SpectrumInputLabel from '../additional/SpectrumInputLabel';
import { convertToDotFormat, getFormOptions } from '../util/commonUtils';
import get from 'lodash/get';

interface WithInput {
  input: any;
  options?: { label: string; value: string }[];
  enumSectionProps?: any;
}

const validateInput = (error: string, hasTouched: boolean) => {
  return hasTouched ? (error ? 'invalid' : 'valid') : undefined;
};

export const SpectrumInputControl = (props: ControlProps & WithInput) => {
  const {
    config: { formTouched },
    description,
    id,
    handleChange,
    errors,
    schema,
    uischema,
    path,
    visible,
    enabled,
    input,
    label,
    data,
    enumSectionProps,
  } = props;

  const [touched, setTouched] = useState(false);
  const placeholder = uischema?.options?.placeholder;
  const tooltip = uischema?.options?.tooltip;
  const alignTooltip = uischema?.options?.alignTooltip;
  const customErrors = uischema?.options?.errors || [];
  const maxLength = schema?.maxLength;
  const showMaxLength = uischema?.options?.showMaxLength;
  const maxLengthMessage = uischema?.options?.maxLengthMessage;
  const labelStyle = uischema?.options?.labelStyle;
  const style = uischema?.options?.style;
  const autoPopulateKey = uischema?.options?.autoPopulateKey;
  const validationRules = uischema?.options?.validationRules;

  const context = useJsonForms();
  const [errorArray, setErrorArray] = useState(context?.core?.errors);
  const [autoPopulateData, setAutoPopulateData] = useState<any>({});

  let customErrorMessage: any;
  let isExternalError: boolean;
  let inputData: any = data;

  useEffect(() => {
    setErrorArray(context?.core?.errors);
  }, [context?.core?.errors]);

  useEffect(() => {
    // When the form is reset, the formTouched is set to false, then make the field as untouched
    if (formTouched === false && touched === true) {
      setTouched(false);
    }
  }, [formTouched]);

  errorArray?.forEach((element: any) => {
    let instancePath;

    // version 2.5
    if (element.dataPath) {
      instancePath = element.dataPath;
    } else {
      // version 3.X
      if (element.keyword === 'required') {
        if (element.instancePath) {
          instancePath =
            convertToDotFormat(element.instancePath) +
            '.' +
            element?.params?.missingProperty;
        } else {
          instancePath = element?.params?.missingProperty;
        }
      } else {
        instancePath = convertToDotFormat(element.instancePath) || path;
      }
    }

    if (
      (instancePath === path || element.externalError) &&
      element.keyword != 'if'
    ) {
      const customError = customErrors[element.keyword] || element.message;
      isExternalError = element.externalError;
      if (customError) {
        customErrorMessage = customError;
      }
    }
  });

  const InnerComponent = input;
  const hasTouched = formTouched || touched;
  let validationState = !isExternalError
    ? validateInput(errors, hasTouched)
    : 'invalid';

  const customRuleValidation = () => {
    for (let i = 0; i < validationRules.length; i += 1) {
      if (validationRules[i].type === 'uniqueValue') {
        const dependentFields = validationRules[i].dependentFields;
        const formDataLocal = context.core.data;
        for (let j = 0; j < dependentFields.length; j += 1) {
          if (
            formDataLocal[path] &&
            formDataLocal[dependentFields[j]] === formDataLocal[path]
          ) {
            validationState = 'invalid';
            return validationRules[i].errorMessage;
          }
        }
      }
    }
  };

  if (validationRules && hasTouched && context?.core?.data) {
    // If there is already a custom error message, then that takes precedence
    customErrorMessage = customErrorMessage || customRuleValidation();
  }

  const onChangeHandler = (path: string, value: any) => {
    handleChange(path, value ? value : undefined);
    setTouched(true);
    if (onChange) onChange(value);
  };

  const formOptions = getFormOptions(context?.core?.uischema);
  const inputControlCallback =
    context?.config?.lifecycleHooks?.onSuccessCallbacks?.inputControl;
  const onErrorMessageCallback = inputControlCallback?.onErrorMessageCallback;
  const onChange = inputControlCallback?.onChange;
  const onLoadInputData = inputControlCallback?.onLoadInputData;

  if (onErrorMessageCallback) {
    onErrorMessageCallback({
      errorArray,
      setErrorArray,
      touched,
      formTouched,
      setTouched,
      path,
    });
  }
  if (onLoadInputData) {
    onLoadInputData({ autoPopulateData, setAutoPopulateData, autoPopulateKey });
  }

  if (Object.keys(autoPopulateData).length > 0) {
    inputData = get(autoPopulateData, autoPopulateKey, '');
    validationState = 'valid';
  }

  const labelComp = (
    <SpectrumInputLabel
      label={label}
      tooltip={tooltip}
      alignTooltip={alignTooltip}
      maxLength={maxLength}
      showMaxLength={showMaxLength}
      value={data}
      maxLengthMessage={maxLengthMessage}
      style={{ ...formOptions?.labelStyle, ...labelStyle }}
    />
  );

  return (
    <div
      hidden={visible === undefined || visible === null ? false : !visible}
      id={id}
      style={{ position: 'relative' }}
    >
      <Flex
        direction='column'
        UNSAFE_style={{ ...formOptions?.controlStyle, ...style }}
      >
        <InnerComponent
          {...props}
          id={id && `${id}-input`}
          data={touched ? data : data || inputData}
          validationState={validationState}
          handleChange={onChangeHandler}
          placeholder={placeholder}
          label={labelComp}
          maxLength={maxLength}
          errorMessage={customErrorMessage}
          description={description}
          enumSectionProps={enumSectionProps}
          isDisabled={!enabled}
        />
      </Flex>
    </div>
  );
};

export default withJsonFormsControlProps(SpectrumInputControl);
